import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";

const CATEGORIES = ["All", "Recovery", "Metabolic", "Cognitive"];

export default function Catalog() {
  const [peptides, setPeptides] = useState([]);
  const [active, setActive] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/peptides").then((r) => {
      setPeptides(r.data);
      setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => {
    let list = peptides;
    if (active !== "All") list = list.filter((p) => p.category === active);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((p) =>
        [p.name, p.chemical_name, p.summary, p.focus].some((x) => x?.toLowerCase().includes(s))
      );
    }
    return list;
  }, [peptides, active, q]);

  const featured = peptides.filter((p) => p.featured).slice(0, 4);

  return (
    <div data-testid="catalog-page">
      {/* Hero band */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <div className="eyebrow mb-3">Shop · Research Peptides</div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-900 tracking-tight leading-[1.05]">
              The peptide library.
            </h1>
            <p className="text-slate-600 max-w-md text-base leading-relaxed">
              Every entry is an informational summary backed by third-party assay documentation.
            </p>
          </div>
        </div>
      </section>

      {/* Featured strip */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16">
          <SectionHeader eyebrow="Featured" title="This week's most-referenced compounds." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {featured.map((p) => <ProductCard key={p.slug} peptide={p} />)}
          </div>
        </section>
      )}

      {/* Filters + search + grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
          <div className="flex flex-wrap items-center gap-2" data-testid="catalog-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase()}`}
                onClick={() => setActive(c)}
                className={`h-10 px-4 rounded-md text-sm transition-colors border ${
                  active === c ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-slate-900"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="relative w-full lg:w-80">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.75} />
            <Input
              data-testid="catalog-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search compounds…"
              className="h-10 pl-9 rounded-md border-slate-300"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-slate-400 font-mono text-xs uppercase tracking-widest">Loading catalog…</div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-slate-200 rounded-md p-16 text-center text-slate-500">
            No compounds match your filters.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((p) => <ProductCard key={p.slug} peptide={p} />)}
          </div>
        )}
      </section>
    </div>
  );
}
